import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { res } from '../../../../common/language/text/res';
import { Strings } from '../../../../common/language/strings';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SecurityFacade } from '../../../../common/facade/SecurityFacade';
import { finalize } from 'rxjs/operators';
import { UserRole } from '../../../../common/model/UserRole';
import { forkJoin, Subscription } from 'rxjs';
import { UserPermission } from '../../../../common/model/UserPermission';
import { RolePermissionsModalComponent } from '../role-permissions-modal/role-permissions-modal.component';
import { Permission } from '../../../../common/model/Permission';
import { SESSION_STORAGE, WebStorageService } from 'ngx-webstorage-service';
import { Permissions } from '../../../../common/model/dictionary/Permissions';

@Component({
  selector: 'app-admin-roles',
  templateUrl: './admin-roles.component.html',
  styleUrls: ['./admin-roles.component.css']
})
export class AdminRolesComponent implements OnInit, OnDestroy {
  // Labels and text
  public pageTitle: res;
  public editLabel: res;
  public searchLabel: res;
  public permissionsLabel: res;

  public errorGettingData: res;

  // Table columns
  public idLabel: res;
  public nameLabel: res;
  public descriptionLabel: res;
  public statusLabel: res;

  // Modal
  private permissionsModalRef: BsModalRef;
  private _onHideModalSubscription: Subscription;

  public loading: boolean;
  public messageLoader: string;
  public alerts: { msg: string, type: string }[];

  public rows: UserRole[];
  public _filteredRows: UserRole[];
  public selected: UserRole[];

  public permissions: UserPermission[];

  public messagesTable: {
    emptyMessage: string,
    totalMessage: string
  };

  public canEdit: boolean;

  constructor(
    public strings: Strings,
    public modalService: BsModalService,
    private securityService: SecurityFacade,
    @Inject(SESSION_STORAGE) private _storage: WebStorageService,
  ) { 
    this.pageTitle = res.Roles;
    this.editLabel = res.Edit;
    this.searchLabel = res.Search;
    this.permissionsLabel = res.Permissions;

    this.errorGettingData = res.ErrorGettingData;

    this.idLabel = res.Identifier;
    this.nameLabel = res.Name;
    this.descriptionLabel = res.Description;
    this.statusLabel = res.Status;

    this.rows = [];
    this._filteredRows = [];
    this.selected = [];

    this.permissions = [];

    this.loading = false;
    this.messageLoader = '';
    this.alerts = [];

    this.messagesTable = {
      emptyMessage: this.strings.GetText(res.ThereIsNoInformation),
      totalMessage: "Total"
    }

    this.canEdit = false;
  }

  ngOnInit(): void {
    this.getRoles();
    this.setPermissions();
  }

  ngOnDestroy(): void {
    if (this._onHideModalSubscription)
      this._onHideModalSubscription.unsubscribe();
  }

  private setPermissions(): void {
    const permissions: Permission[] = this._storage.get('permissions');

    this.canEdit = permissions.some(p => p.name === Permissions.AdminRolePermissionsEdit);
  }

  public async getRoles(): Promise<void> {
    this.loading = true;

    forkJoin([
      this.securityService.GetRoles(),
      this.securityService.GetPermissions()
    ]).pipe(finalize(() => this.loading = false))
    .subscribe(([roles, permissions]) => {
      if (!roles.completed || !permissions.completed)
        return;

      this.rows = roles.data;
      this._filteredRows = [...roles.data];

      this.permissions = permissions.data;

    }, error => {
      this.addAlert(`<strong> ${this.strings.GetText(this.errorGettingData)} </strong>`, 'danger');
    });
  }

  public updateFilter(event): void {
    const val = event.target.value.toLowerCase();

    const keys = Object.keys(this._filteredRows[0]);

    this.rows = this._filteredRows.filter(function (item) {
      for (let i = 0; i < keys.length; i++) {
        if (item[keys[i]] != null && (item[keys[i]].toString().toLowerCase().indexOf(val) !== -1 || !val)) {
            return true;
        }
      }
    });
  }

  public isSomeRowSelected(): boolean {
    return this.selected.length > 0;
  }

  public showPermissionsModal(): void {
    if (this.selected.length == 0)
      return;

    this.permissionsModalRef = this.modalService.show(RolePermissionsModalComponent, {
      initialState: {
        role: this.selected[0],
        permissions: this.permissions
      },
      class: 'modal-xl'
    });

    this._onHideModalSubscription = this.permissionsModalRef.content.onClose$.subscribe((response: number) => {
      if (response === 1) {
        this.addAlert(`<strong>${this.strings.GetText(res.RolesUpdatedSuccessfully)}<strong>`, 'success');
        this.selected = [];
      } else if (response === -1) {
        this.addAlert(`<strong>${this.strings.GetText(res.RolesUpdateFailed)}<strong>`, 'danger');
        this.selected = [];
      }
    });
  }

  public addAlert(message: string, type: string): void {
    this.alerts = [];
    this.alerts.push({
      type: type,
      msg: message
    });
  }

  public onAlertClose(dismissedAlert: { msg: string, type: string }): void {
    this.alerts = this.alerts.filter(alert => alert !== dismissedAlert);
  }

}
